import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";

const EDIT_FIELDS = [
  { key: "NM_CEREURB",               label: "Nome como esta no CERURB"                        },
  // { key: "NUCLEO_CASALEGAL_PAINEIS", label: "Nome Núcleo Casa Legal (Painéis)" },
  { key: "NUCLEO",                   label: "Nome do Núcleo"                   },
];

const EMPTY_FORM = { NM_CERURB: "", NUCLEO_CASALEGAL_PAINEIS: "", NUCLEO: "" };

async function readFieldsFromZip(file, encoding = "utf-8") {
  const zip = await JSZip.loadAsync(file);
  const names = Object.keys(zip.files).filter(n => !zip.files[n].dir);
  if (!names.find(n => n.toLowerCase().endsWith(".shp")))
    throw new Error("Arquivo .shp não encontrado no zip.");
  const dbfEntry = names.find(n => n.toLowerCase().endsWith(".dbf"));
  if (!dbfEntry) return {};
  const buf = await zip.files[dbfEntry].async("arraybuffer");
  return parseFirstRecord(buf, encoding);
}

function parseFirstRecord(buf, encoding = "utf-8") {
  try {
    const view = new DataView(buf);
    const headerBytes = view.getUint16(8, true);
    const decoder = new TextDecoder(encoding);
    const fields = [];
    let offset = 32;
    while (offset + 32 <= headerBytes) {
      const name = String.fromCharCode(...new Uint8Array(buf, offset, 11)).replace(/\0.*/g, "").trim();
      if (!name || name.charCodeAt(0) === 13) break;
      fields.push({ name, len: view.getUint8(offset + 16) });
      offset += 32;
    }
    const row = {};
    let rec = headerBytes + 1;
    for (const f of fields) {
      if (rec + f.len > buf.byteLength) break;
      row[f.name] = decoder.decode(new Uint8Array(buf, rec, f.len)).trim();
      rec += f.len;
    }
    return row;
  } catch { return {}; }
}

export default function ShapefileUploadPage() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const API_KEY = process.env.REACT_APP_API_KEY;

  const [file, setFile]           = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [encoding, setEncoding]   = useState("utf-8");
  const [parsing, setParsing]     = useState(false);
  const [parseErr, setParseErr]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg]             = useState(null);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setParseErr(null);
    setForm(EMPTY_FORM);
    setParsing(true);
    readFieldsFromZip(file, encoding)
      .then(raw => {
        if (cancelled) return;
        setForm({
          NM_CERURB:               raw.NM_CERURB               ?? "",
          NUCLEO_CASALEGAL_PAINEIS: raw.NUCLEO_CASALEGAL_PAINEIS ?? "",
          NUCLEO:                  raw.NUCLEO                  ?? "",
        });
      })
      .catch(err => { if (!cancelled) setParseErr(err.message || "Erro ao ler o arquivo."); })
      .finally(() => { if (!cancelled) setParsing(false); });
    return () => { cancelled = true; };
  }, [file, encoding]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    setFile(selected);
    setMsg(null);
    if (!selected) { setForm(EMPTY_FORM); setParseErr(null); }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    setMsg(null);
    const fd = new FormData();
    fd.append("shapefile", file);
    fd.append("overrides", JSON.stringify(form));
    fd.append("encoding", encoding);
    try {
      const res = await axios.post(`${API_URL}/dados/nucleos/shapefile`, fd, {
        headers: { Authorization: `Bearer ${API_KEY}` },
      });
      setMsg({ ok: true, text: res.data.message });
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || "Erro ao enviar shapefile." });
    } finally {
      setUploading(false);
    }
  };

  const canSubmit = file && !uploading && !parsing;

  return (
    <PageShell>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate(-1)} style={backBtnStyle}>← Voltar</button>
        <h2 style={{ margin: 0, fontSize: 18, color: "#1976d2" }}>Enviar Shapefile</h2>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Arquivo .zip <span style={{ color: "#888", fontWeight: "normal", fontSize: 11 }}>(contendo .shp, .dbf, .prj)</span>
        </label>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={filePickerStyle}>
            📂 {file ? file.name : "Selecionar arquivo .zip"}
            <input type="file" accept=".zip" style={{ display: "none" }} onChange={handleFileChange} />
          </label>
          {file && (
            <span
              onClick={() => { setFile(null); setForm(EMPTY_FORM); setMsg(null); setParseErr(null); }}
              style={{ fontSize: 12, color: "#c62828", cursor: "pointer" }}
            >
              ✕ remover
            </span>
          )}
          {parsing && <span style={{ fontSize: 12, color: "#888" }}>Lendo campos...</span>}
        </div>
        {parseErr && <div style={alertStyle("#fff3e0", "#e65100")}>{parseErr}</div>}
      </div>

      <div style={{ marginBottom: 20, maxWidth: 480 }}>
        <label style={labelStyle}>Codificação do arquivo (.dbf)</label>
        <select
          value={encoding}
          onChange={e => setEncoding(e.target.value)}
          style={{ ...inputStyle, fontSize: 13 }}
        >
          <option value="utf-8">UTF-8 (padrão)</option>
          <option value="iso-8859-1">Latin-1 / ISO-8859-1</option>
          <option value="windows-1252">CP1252 / Windows-1252</option>
        </select>
        <span style={{ fontSize: 11, color: "#888", marginTop: 4, display: "block" }}>
          Altere se os caracteres especiais aparecerem incorretos nos campos abaixo.
        </span>
      </div>

      {file && !parsing && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 480, marginBottom: 20 }}>
          {EDIT_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label style={labelStyle}>{label}</label>
              <input
                value={form[key]}
                onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                style={inputStyle}
              />
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div style={{
          ...alertStyle(msg.ok ? "#e8f5e9" : "#ffebee", msg.ok ? "#2e7d32" : "#c62828"),
          fontSize: 14,
          fontWeight: msg.ok ? "bold" : "normal",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          {msg.ok ? "✅" : "❌"} {msg.text}
          {msg.ok && <span style={{ fontSize: 12, fontWeight: "normal", color: "#555", marginLeft: 4 }}>Redirecionando...</span>}
        </div>
      )}

      {file && !parsing && (
        <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end", maxWidth: 480 }}>
          <button onClick={() => navigate(-1)} style={btnStyle("#757575")} disabled={uploading}>Cancelar</button>
          <button onClick={handleSubmit} disabled={!canSubmit} style={btnStyle(canSubmit ? "#2e7d32" : "#90a4ae")}>
            {uploading ? "Enviando..." : "Enviar e importar"}
          </button>
        </div>
      )}
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", justifyContent: "center", padding: "40px 16px" }}>
      <div style={{ width: "100%", maxWidth: 700, background: "#fff", borderRadius: 8, padding: 32, boxShadow: "0 2px 12px rgba(0,0,0,0.1)", height: "fit-content" }}>
        {children}
      </div>
    </div>
  );
}

const labelStyle     = { display: "block", fontSize: 12, color: "#555", fontWeight: "bold", marginBottom: 4 };
const inputStyle     = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 14, outline: "none" };
const filePickerStyle = { display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 14px", border: "1px dashed #1976d2", borderRadius: 4, color: "#1976d2", fontSize: 13 };
const backBtnStyle   = { padding: "6px 14px", background: "none", border: "1px solid #1976d2", color: "#1976d2", borderRadius: 4, cursor: "pointer", fontSize: 13 };
const btnStyle       = (bg) => ({ padding: "8px 20px", background: bg, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 });
const alertStyle     = (bg, color) => ({ marginTop: 10, padding: "10px 14px", background: bg, color, borderRadius: 4, fontSize: 13 });
